import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import { Weapon, EquipmentTier } from '@astra/shared';
import { assetLoader } from '../core/AssetLoader';

// Tier-based beam colors (red spectrum)
const TIER_COLORS: Record<EquipmentTier, { beam: number; glow: number }> = {
  1: { beam: 0xff0000, glow: 0xff4444 },    // Red
  2: { beam: 0xff2200, glow: 0xff5544 },    // Brighter red
  3: { beam: 0xff3300, glow: 0xff6644 },    // Orange-red
  4: { beam: 0xff4400, glow: 0xff7755 },    // Intense orange-red
};

// Particle texture cache
let particleTextureGenerated = false;
let particleTexture: Texture | null = null;

interface LaserSource {
  x: number;
  y: number;
  rotation: number; // In degrees
  noseOffset?: number; // Distance from center to ship nose (for beam start)
}

export interface LaserTarget {
  id: string;
  type: 'bot' | 'player' | 'asteroid';
  x: number;
  y: number;
  radius: number;
}

interface HitParticle {
  sprite: Sprite;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}

export interface LaserUpdateResult {
  isActive: boolean;
  beamStartX: number;
  beamStartY: number;
  beamEndX: number;
  beamEndY: number;
}

export class CombatLaser extends Container {
  private source: LaserSource;
  private weapon: Weapon;

  // Beam graphics
  private beamGraphics: Graphics;
  private glowGraphics: Graphics;

  // Damage tracking
  private currentTarget: LaserTarget | null = null;
  private timeOnTarget: number = 0;        // Time in ms beam has been on current target
  private currentDamageMultiplier: number = 1.0;

  // Heat tracking (exposed to Player for UI)
  public currentHeat: number = 0;
  public isOverheated: boolean = false;

  // Visual effects
  private pulseTime: number = 0;
  private hitParticles: HitParticle[] = [];
  private parentContainer: Container | null = null;

  // Beam state
  private isActive: boolean = false;
  private beamStartX: number = 0;
  private beamStartY: number = 0;
  private beamEndX: number = 0;      // Full range endpoint (for collision checking)
  private beamEndY: number = 0;
  private visualEndX: number = 0;    // Where to visually draw beam (may stop at target)
  private visualEndY: number = 0;
  private hitSomething: boolean = false;  // Whether beam hit a target this frame

  // Ship nose offset (distance from center to front of ship)
  // Default 25 for backwards compat, overridden by source.noseOffset
  private noseOffset = 25;

  constructor(source: LaserSource, weapon: Weapon) {
    super();
    this.source = source;
    if (source.noseOffset !== undefined) {
      this.noseOffset = source.noseOffset;
    }
    this.weapon = weapon;

    // Ensure particle texture exists
    this.ensureParticleTextureExists();

    this.glowGraphics = new Graphics();
    this.beamGraphics = new Graphics();
    this.addChild(this.glowGraphics);
    this.addChild(this.beamGraphics);

    this.zIndex = 15;
  }

  private ensureParticleTextureExists(): void {
    if (particleTextureGenerated && particleTexture) return;

    const gfx = new Graphics();
    gfx.star(4, 4, 4, 4, 2);
    gfx.fill({ color: 0xffffff, alpha: 1 });
    particleTexture = assetLoader.generateTexture(gfx, 'combat-laser-particle');
    gfx.destroy();
    particleTextureGenerated = true;
  }

  /**
   * Set parent container for particle effects
   */
  setParticleParent(parent: Container): void {
    this.parentContainer = parent;
  }

  /**
   * Start firing the beam
   */
  activate(): void {
    this.isActive = true;
    // Don't reset heat - it persists between activations
  }

  /**
   * Stop firing the beam
   */
  deactivate(): void {
    this.isActive = false;
    this.currentTarget = null;
    this.timeOnTarget = 0;
    this.currentDamageMultiplier = 1.0;
    this.beamGraphics.clear();
    this.glowGraphics.clear();
  }

  /**
   * Update source position (player ship)
   */
  updateSource(source: LaserSource): void {
    this.source = source;
    if (source.noseOffset !== undefined) {
      this.noseOffset = source.noseOffset;
    }
  }

  /**
   * Check if beam is currently active
   */
  isBeamActive(): boolean {
    return this.isActive && !this.isOverheated;
  }

  /**
   * Get the current damage multiplier for UI display
   */
  getDamageMultiplier(): number {
    return this.currentDamageMultiplier;
  }

  /**
   * Update method called every frame
   * @returns beam endpoint info for collision checking by GameScene
   */
  update(delta: number, pointerWorldX: number, pointerWorldY: number): LaserUpdateResult | null {
    const dt = delta / 1000;

    // Reset hit state each frame (GameScene will call setHitPoint if something is hit)
    this.hitSomething = false;

    // Handle cooldown when not active or overheated
    if (!this.isActive || this.isOverheated) {
      this.beamGraphics.clear();
      this.glowGraphics.clear();

      // Cool down
      if (this.currentHeat > 0) {
        this.currentHeat = Math.max(0, this.currentHeat - (this.weapon.stats.cooldownRate ?? 15) * dt);
      }

      // Check if overheat cooldown is complete
      if (this.isOverheated && this.currentHeat <= 0) {
        this.isOverheated = false;
      }

      // Still update particles even when not firing
      this.updateHitParticles(delta);

      return null;
    }

    // Update heat while firing
    const heatRate = this.weapon.stats.beamHeatRate ?? 15;
    this.currentHeat += heatRate * dt;

    if (this.currentHeat >= (this.weapon.stats.overheatThreshold ?? 100)) {
      this.isOverheated = true;
      this.deactivate();
      return null;
    }

    // Calculate beam start position (ship nose)
    const noseAngle = (this.source.rotation - 90) * (Math.PI / 180);
    this.beamStartX = this.source.x + Math.cos(noseAngle) * this.noseOffset;
    this.beamStartY = this.source.y + Math.sin(noseAngle) * this.noseOffset;

    // Calculate beam direction toward pointer - ALWAYS extend to full range
    const dx = pointerWorldX - this.beamStartX;
    const dy = pointerWorldY - this.beamStartY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const range = this.weapon.stats.range;

    // Normalize direction and extend to full range
    const normX = dist > 0 ? dx / dist : 0;
    const normY = dist > 0 ? dy / dist : 1;
    this.beamEndX = this.beamStartX + normX * range;
    this.beamEndY = this.beamStartY + normY * range;

    // Default visual end is full range (GameScene may shorten via setHitPoint)
    this.visualEndX = this.beamEndX;
    this.visualEndY = this.beamEndY;

    // Update particles (draw beam is called after GameScene sets hit point)
    this.updateHitParticles(delta);

    return {
      isActive: true,
      beamStartX: this.beamStartX,
      beamStartY: this.beamStartY,
      beamEndX: this.beamEndX,
      beamEndY: this.beamEndY,
    };
  }

  /**
   * Called by GameScene when beam hits a target to set where beam visually stops
   */
  setHitPoint(x: number, y: number): void {
    this.visualEndX = x;
    this.visualEndY = y;
    this.hitSomething = true;
  }

  /**
   * Called after collision checking to draw the beam at correct visual endpoint
   */
  drawBeamVisual(delta: number): void {
    if (!this.isActive || this.isOverheated) return;

    this.drawBeam(delta);

    // Spawn particles at visual beam end (where it hits or max range)
    this.spawnHitParticles();
  }

  /**
   * Called by GameScene when beam hits a target
   * @returns the damage to apply this frame
   */
  registerHit(target: LaserTarget, delta: number): number {
    const dt = delta / 1000;

    // Check if same target as last frame
    if (this.currentTarget?.id === target.id && this.currentTarget?.type === target.type) {
      // Accumulate time on target
      this.timeOnTarget += delta;

      // Increase damage multiplier based on time on target
      const rampUp = this.weapon.stats.beamDamageRampUp ?? 0.3;
      const maxMult = this.weapon.stats.maxDamageMultiplier ?? 2.0;

      this.currentDamageMultiplier = Math.min(
        maxMult,
        1.0 + (rampUp * this.timeOnTarget / 1000)
      );
    } else {
      // New target - reset multiplier
      this.currentTarget = target;
      this.timeOnTarget = 0;
      this.currentDamageMultiplier = 1.0;
    }

    // Calculate damage for this frame (base DPS * multiplier * deltaTime)
    const baseDamage = this.weapon.stats.damage;
    const damage = baseDamage * this.currentDamageMultiplier * dt;

    return damage;
  }

  /**
   * Called when beam is no longer hitting a target
   */
  clearTarget(): void {
    this.currentTarget = null;
    this.timeOnTarget = 0;
    this.currentDamageMultiplier = 1.0;
  }

  /**
   * Cool down when not firing (called externally if needed)
   */
  coolDown(delta: number): void {
    const dt = delta / 1000;
    const cooldownRate = this.weapon.stats.cooldownRate ?? 15;
    this.currentHeat = Math.max(0, this.currentHeat - cooldownRate * dt);

    if (this.isOverheated && this.currentHeat <= 0) {
      this.isOverheated = false;
    }
  }

  private drawBeam(delta: number): void {
    this.pulseTime += delta / 1000;

    const colors = TIER_COLORS[this.weapon.tier] || TIER_COLORS[1];
    const pulse = Math.sin(this.pulseTime * 8) * 0.3 + 0.7;
    const baseWidth = 2 + (this.weapon.tier * 0.5);
    const currentWidth = baseWidth * pulse;

    // Clear previous frame
    this.glowGraphics.clear();
    this.beamGraphics.clear();

    // Draw beam to visual endpoint (may be shortened if hitting target)
    // Draw glow (outer)
    this.glowGraphics.moveTo(this.beamStartX, this.beamStartY);
    this.glowGraphics.lineTo(this.visualEndX, this.visualEndY);
    this.glowGraphics.stroke({ width: currentWidth + 6, color: colors.glow, alpha: 0.2 });

    // Draw main beam
    this.beamGraphics.moveTo(this.beamStartX, this.beamStartY);
    this.beamGraphics.lineTo(this.visualEndX, this.visualEndY);
    this.beamGraphics.stroke({ width: currentWidth + 2, color: colors.beam, alpha: 0.8 });

    // Draw bright core
    this.beamGraphics.moveTo(this.beamStartX, this.beamStartY);
    this.beamGraphics.lineTo(this.visualEndX, this.visualEndY);
    this.beamGraphics.stroke({ width: 1, color: 0xffffff, alpha: 0.6 });
  }

  private spawnHitParticles(): void {
    if (!this.parentContainer || !particleTexture) return;

    // More particles when hitting something, fewer at max range
    const spawnChance = this.hitSomething ? 0.5 : 0.15;
    if (Math.random() > spawnChance) return;

    const colors = TIER_COLORS[this.weapon.tier] || TIER_COLORS[1];
    const particleColors = [colors.beam, colors.glow, 0xffffff];

    const sprite = new Sprite(particleTexture);
    sprite.anchor.set(0.5);
    // Spawn at visual end point (where beam stops)
    sprite.position.set(this.visualEndX, this.visualEndY);
    sprite.tint = particleColors[Math.floor(Math.random() * particleColors.length)];
    sprite.scale.set(0.2 + Math.random() * 0.2);
    sprite.zIndex = 16;
    this.parentContainer.addChild(sprite);

    const angle = Math.random() * Math.PI * 2;
    const speed = 20 + Math.random() * 40;

    this.hitParticles.push({
      sprite,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 150 + Math.random() * 150,
      maxLife: 150 + Math.random() * 150,
    });
  }

  private updateHitParticles(delta: number): void {
    const dt = delta / 1000;

    for (let i = this.hitParticles.length - 1; i >= 0; i--) {
      const particle = this.hitParticles[i];

      // Move
      particle.sprite.x += particle.vx * dt;
      particle.sprite.y += particle.vy * dt;

      // Fade
      particle.life -= delta;
      particle.sprite.alpha = Math.max(0, particle.life / particle.maxLife);

      // Scale down
      const scale = 0.2 * (particle.life / particle.maxLife);
      particle.sprite.scale.set(scale);

      // Remove dead particles
      if (particle.life <= 0) {
        particle.sprite.destroy();
        this.hitParticles.splice(i, 1);
      }
    }
  }

  /**
   * Trigger a burst of particles (e.g., on hitting a target)
   */
  triggerHitBurst(): void {
    if (!this.parentContainer || !particleTexture) return;

    const colors = TIER_COLORS[this.weapon.tier] || TIER_COLORS[1];
    const particleColors = [colors.beam, colors.glow, 0xffffff];

    for (let i = 0; i < 8; i++) {
      const sprite = new Sprite(particleTexture);
      sprite.anchor.set(0.5);
      // Spawn at visual endpoint (where beam actually stops)
      sprite.position.set(this.visualEndX, this.visualEndY);
      sprite.tint = particleColors[Math.floor(Math.random() * particleColors.length)];
      sprite.scale.set(0.3 + Math.random() * 0.2);
      sprite.zIndex = 16;
      this.parentContainer.addChild(sprite);

      const angle = Math.random() * Math.PI * 2;
      const speed = 40 + Math.random() * 40;

      this.hitParticles.push({
        sprite,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 200 + Math.random() * 150,
        maxLife: 200 + Math.random() * 150,
      });
    }
  }

  destroy(): void {
    this.beamGraphics.destroy();
    this.glowGraphics.destroy();

    // Clean up remaining particles
    for (const particle of this.hitParticles) {
      particle.sprite.destroy();
    }
    this.hitParticles = [];

    super.destroy();
  }
}
